import { Component, computed, effect, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  ReportTypeEnum,
  type KlReportStartPayload,
  type ReportViewType,
} from '@shared/models/report-type.model';
import { PrimeNGModule } from '@shared/primeng/primeng.module';
import { KlLoader } from '@shared/components/kl-loader/kl-loader';

export type { KlReportStartPayload, ReportViewType } from '@shared/models/report-type.model';

/**
 * Report page shell: header (filters title + start, menu toggle + title), collapsible filter sidebar, results projection.
 * Matches legacy `kl-report-filter2` layout.
 */
@Component({
  selector: 'kl-report',
  standalone: true,
  imports: [FormsModule, TranslateModule, PrimeNGModule, KlLoader],
  templateUrl: './kl-report.html',
  styleUrl: './kl-report.scss',
  host: {
    '[class.kl-report--sidebar-collapsed]': '!filterPanelOpen()',
    '[class.kl-report--fullscreen]': 'fullscreen()',
  },
})
export class KlReport {
  /** Translated report title (consumer supplies `| translate`). */
  readonly reportTitle = input.required<string>();

  /** Report data / results area only (does not cover filters sidebar). */
  readonly loading = input(false);

  /** Filter options (e.g. dropdown data) loading — loader over sidebar only. */
  readonly filtersLoading = input(false);

  /** Sidebar open state (two-way). */
  readonly filterPanelOpen = model(true);

  /** Full-bleed overlay mode (two-way); consumer toolbar may toggle. */
  readonly fullscreen = model(false);

  /**
   * Report-type row in the filter sidebar (Liste / Matris / Excel).
   * Opt in per report page; legacy `kl-report-filter2` always showed it.
   */
  readonly showReportType = input(false);

  /** i18n key for the sidebar start button (default: report runner). */
  readonly startButtonKey = input('start_report');

  /** Selected report view type (Liste / Matris / Excel). Two-way with parent. */
  readonly reportType = model<ReportViewType>(ReportTypeEnum.GRID);

  /** When true, show projected results immediately (skip defer-until-Start). */
  readonly showResultsOnLoad = input(false);

  /** Becomes true after the user clicks Start; stays true for subsequent runs. */
  readonly resultsVisible = signal(false);

  readonly showResults = computed(() => this.resultsVisible() || this.showResultsOnLoad());

  readonly startReport = output<KlReportStartPayload>();

  readonly reportTypeOptions = [
    { label: 'list_report', value: ReportTypeEnum.GRID },
    { label: 'matrix_report', value: ReportTypeEnum.PIVOT },
    { label: 'excel_report', value: ReportTypeEnum.EXCEL_SHEET },
  ];

  /** Set by Start; the filter panel closes only once that run actually starts loading (validation errors keep it open). */
  private closePanelOnLoad = false;

  constructor() {
    effect(() => {
      if (this.loading() && this.closePanelOnLoad) {
        this.closePanelOnLoad = false;
        this.filterPanelOpen.set(false);
      }
    });
  }

  onReportTypeSelect(value: ReportViewType): void {
    this.reportType.set(value);
  }

  onStartClick(): void {
    this.closePanelOnLoad = true;
    this.resultsVisible.set(true);
    this.startReport.emit({ report_type: this.reportType() });
  }

  toggleSidebar(): void {
    this.filterPanelOpen.update((v) => !v);
  }
}
